import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Options } from '../../model/options';
import { GymFinderService } from '../../service/gymfinder.service';
import { BaseDirective } from '@egym/ngx-adm-int';
import { Activity } from 'src/app/model/options/activity';
import { Item } from './checkboxes/checkboxes.component';
import { Service } from 'src/app/model/options/service';
import { GymUtils } from '../../util/gym-util';
import { NetworkService } from 'src/app/service/network.service';
import { GymType, MembershipType, NetworkType } from '../../model/gym';
import { CreditsConfig } from '../../model/credits/credits-config';

@Component({
  selector: 'gymfinder-options',
  templateUrl: './options.component.html',
  styleUrls: ['./options.component.scss'],
  standalone: false,
})
export class OptionsComponent extends BaseDirective<Options> implements OnInit {
  @Input()
  options: Options | undefined;

  @Input()
  editMode: boolean;

  @Input()
  creditsFeatureFlag: boolean;

  @Input()
  creditsConfig: CreditsConfig | undefined;

  @Output()
  optionsChange = new EventEmitter<Options>();

  @Output()
  creditsConfigChange = new EventEmitter<CreditsConfig>();

  protected readonly GymType = GymType;

  defaultOptions: Options | undefined;

  disabledEquipments: string[] = [
    'EGYM_SMART_FLEX',
    'EGYM_SMART_STRENGTH',
    'EGYM_FITNESS_HUB',
    'EGYM_POWER_EQUIPMENT',
  ];

  gymTypeRules: Record<
    GymType,
    { network: NetworkType; membershipType: MembershipType }
  > = {
    [GymType.WELLPASS_DACH_UNLIMITED]: {
      network: NetworkType.DACH,
      membershipType: MembershipType.UNLIMITED,
    },
    [GymType.WELLPASS_DACH_PLUS1]: {
      network: NetworkType.DACH,
      membershipType: MembershipType.PLUS_1,
    },
    [GymType.WELLPASS_FR_BE_UNLIMITED]: {
      network: NetworkType.FR_BE,
      membershipType: MembershipType.UNLIMITED,
    },
    [GymType.WELLPASS_FR_BE_CREDITS]: {
      network: NetworkType.FR_BE,
      membershipType: MembershipType.CREDITS,
    },
    [GymType.WELLPASS_FR_BE_PLUS1]: {
      network: NetworkType.FR_BE,
      membershipType: MembershipType.PLUS_1,
    },
    [GymType.WELLPASS_US_UNLIMITED]: {
      network: NetworkType.US,
      membershipType: MembershipType.UNLIMITED,
    },
  };

  gymTypeAutoSelectMap: Partial<Record<GymType, GymType>> = {
    [GymType.WELLPASS_FR_BE_CREDITS]: GymType.WELLPASS_FR_BE_PLUS1,
    [GymType.WELLPASS_FR_BE_UNLIMITED]: GymType.WELLPASS_FR_BE_PLUS1,
    [GymType.WELLPASS_DACH_PLUS1]: GymType.WELLPASS_DACH_UNLIMITED,
  };

  disabledGymTypes: string[] = [];

  protected readonly CASHBACK_PARTNER = 'ON_SITE_PAYMENT_AND_REIMBURSEMENT';

  constructor(
    snackBar: MatSnackBar,
    private readonly gymFinderService: GymFinderService,
    private readonly networkService: NetworkService
  ) {
    super(snackBar);
  }

  ngOnInit() {
    this.networkService.network$.subscribe(network => {
      this.loadOptions(network);
    });
  }

  private readonly loadOptions = (network: NetworkType | null) => {
    this.gymFinderService.getOptions(network).subscribe({
      next: value => {
        this.defaultOptions = value;
        this.disabledGymTypes = this.calculateDisabledGymTypes();
      },
      error: () => {
        this.openSnackBar('Error while getting the default options');
      },
    });
  };

  public get mainActivity(): string | undefined {
    return this.options?.activities?.[0]?.activity || '';
  }

  public set mainActivity(value: string | undefined) {
    if (this.options) {
      // Move the main activity to the first position in options.activities
      const mainActivity =
        this.options.activities.find(activity => activity.activity === value) ??
        this.defaultOptions?.activities.find(
          activity => activity.activity === value
        );

      const activities = mainActivity
        ? [
            { ...mainActivity, locked: true }, // Lock the main activity when selecting it
            ...this.options.activities.filter(
              activity => activity.activity !== value
            ),
          ]
        : this.options.activities;

      this.onOptionsChanged({ field: 'activities', value: activities });
    }
  }

  getTranslation = (
    field:
      | 'activities'
      | 'equipmentsToBring'
      | 'gymEquipments'
      | 'gymInfos'
      | 'gymTypes'
      | 'services'
      | 'geniusFeatures'
  ) => GymUtils.getOptionsTranslation(field);

  private calculateDisabledGymTypes = (gymTypes?: GymType[]): string[] => {
    const allGymTypes = this.defaultOptions?.gymTypes ?? [];
    const typesToCheck = gymTypes ?? this.options?.gymTypes;

    if (!typesToCheck?.length) {
      return [];
    }

    const selectedRules = typesToCheck
      .map(t => this.gymTypeRules[t])
      .filter(Boolean);

    // This could happen if we add a new gym type in backend and it's not yet supported
    if (!selectedRules.length) {
      return [];
    }

    const selectedNetwork = selectedRules[0].network;
    const hasUnlimited = selectedRules.some(
      rule => rule.membershipType === MembershipType.UNLIMITED
    );
    const hasCredits = selectedRules.some(
      rule => rule.membershipType === MembershipType.CREDITS
    );

    return allGymTypes.filter(type => {
      const rule = this.gymTypeRules[type];
      if (!rule) return false;

      // Rule 1: gym types from different networks are incompatible
      if (selectedNetwork && rule.network !== selectedNetwork) return true;

      // Rule 2: credits vs unlimited incompatibility
      if (hasUnlimited && rule.membershipType === MembershipType.CREDITS)
        return true;
      if (hasCredits && rule.membershipType === MembershipType.UNLIMITED)
        return true;

      return false;
    });
  };

  onActivitiesChanged = (items: Item[]) => {
    const activities = items.map(item => ({
      activity: item.name,
      charged: item.charged,
      locked: item.locked,
    }));
    this.onOptionsChanged({ field: 'activities', value: activities });
  };

  onServicesChanged = (items: Item[]) => {
    const services = items.map(item => ({
      service: item.name,
      charged: item.charged,
      locked: item.locked,
    }));
    this.onOptionsChanged({ field: 'services', value: services });
  };

  onGymTypesChanged = (value: string[]) => {
    const nextGymTypes = [...value] as GymType[];

    this.resetCreditsConfig(nextGymTypes);

    const prevGymTypes = this.options?.gymTypes ?? [];
    const removedGymTypes = prevGymTypes.filter(
      t => !nextGymTypes.includes(t as GymType)
    );

    for (const [triggerType, autoSelectGymType] of Object.entries(
      this.gymTypeAutoSelectMap
    ) as [GymType, GymType][]) {
      if (
        nextGymTypes.includes(triggerType) &&
        !nextGymTypes.includes(autoSelectGymType) &&
        !removedGymTypes.includes(autoSelectGymType)
      ) {
        nextGymTypes.push(autoSelectGymType);
      }
    }

    this.disabledGymTypes = this.calculateDisabledGymTypes(nextGymTypes);
    this.onOptionsChanged({ field: 'gymTypes', value: nextGymTypes });
  };

  public filterGymInfos(
    list: string[] | undefined,
    isCashback: boolean
  ): string[] {
    const items = list || [];
    return isCashback
      ? items.filter(item => item === this.CASHBACK_PARTNER)
      : items.filter(item => item !== this.CASHBACK_PARTNER);
  }

  public onGymInfosChanged(newValues: string[], isCashback: boolean): void {
    const currentInfos = this.options?.gymInfos || [];

    const keptItems = isCashback
      ? currentInfos.filter(item => item !== this.CASHBACK_PARTNER)
      : currentInfos.filter(item => item === this.CASHBACK_PARTNER);
    const updatedInfos = [...keptItems, ...newValues];

    this.onOptionsChanged({ field: 'gymInfos', value: updatedInfos });
  }

  private resetCreditsConfig(gymTypes: string[]) {
    if (!gymTypes.includes(GymType.WELLPASS_FR_BE_CREDITS)) {
      this.onCreditsConfigChanged();
    }
  }

  public onCreditsConfigChanged = (value?: CreditsConfig) => {
    this.creditsConfigChange.emit(value);
  };

  public onOptionsChanged = <K extends keyof Options>({
    field,
    value,
  }: {
    field: K;
    value: Options[K];
  }) => {
    const newOptions: Options = this.options || new Options();
    newOptions[field] = value;
    this.optionsChange.emit(newOptions);
  };

  convertActivitiesToItems = (activities: Activity[]): Item[] =>
    this.convertToItems({ value: activities, field: 'activity' });

  convertServicesToItems = (services: Service[]): Item[] =>
    this.convertToItems({ value: services, field: 'service' });

  private readonly convertToItems = <T extends Activity | Service>({
    value,
    field,
  }: {
    value: T[];
    field: keyof Omit<T, 'charged' | 'locked'>;
  }): Item[] =>
    value.map(i => ({
      name: i[field] as string,
      charged: i.charged,
      locked: i.locked,
    }));
}
